import { Cartesian3 } from 'cesium';
import { config } from '../app/config';
import type { TrackStation } from './types';

export interface WireSpan {
  /** Wire endpoints (ECEF) — spans are straight lines between supports. */
  points: Cartesian3[];
}

export interface OhleGeometry {
  contact: WireSpan[];
  /** Top (catenary) wire, carried by the horizontal cantilever arm. */
  top: WireSpan[];
  /** Mast base + top pairs (ECEF) for drawing supports. */
  masts: Array<{ base: Cartesian3; top: Cartesian3 }>;
  /** Cantilever members per mast: horizontal arm + rising arm (ECEF pairs). */
  arms: Array<{ from: Cartesian3; to: Cartesian3 }>;
}

/**
 * Build overhead line equipment along an electrified track.
 *
 * Dimensions follow the QR cantilever drawing documented in config.track.ohle:
 * 7.0 m masts offset 3.0 m from the centreline, a horizontal cantilever arm at
 * 5.85 m above rail level carrying the top (catenary) wire above the
 * centreline, and a second arm rising from 4.5 m on the mast to the cantilever
 * tip. Wires are tensioned, so each span is a STRAIGHT line between mast
 * attachment points (on curves the wire chords the centreline, as real OHLE
 * does). Contact wire height (5.0 m) is an assumption — see config. Droppers
 * are not modelled.
 */
export function buildOhle(stations: TrackStation[]): OhleGeometry {
  const { ohle, railHeightAboveGround } = config.track;
  const out: OhleGeometry = { contact: [], top: [], masts: [], arms: [] };
  if (stations.length < 2) return out;

  // Pick mast stations by arc length.
  const mastIdx: number[] = [];
  let nextMast = 0;
  for (let i = 0; i < stations.length; i++) {
    if (stations[i].distance >= nextMast) {
      mastIdx.push(i);
      nextMast += ohle.mastSpacing;
    }
  }
  if (mastIdx[mastIdx.length - 1] !== stations.length - 1) {
    mastIdx.push(stations.length - 1);
  }

  /** Point offset from a station: `lateral` m along `right`, `lift` m along `up`. */
  const at = (s: TrackStation, lateral: number, lift: number): Cartesian3 => {
    const p = Cartesian3.clone(s.position);
    Cartesian3.add(p, Cartesian3.multiplyByScalar(s.right, lateral, new Cartesian3()), p);
    Cartesian3.add(p, Cartesian3.multiplyByScalar(s.up, lift, new Cartesian3()), p);
    return p;
  };

  // Wire attachment points above the centreline at each mast station.
  const contactAt = (s: TrackStation) => at(s, 0, ohle.contactWireHeightAboveRail);
  const topAt = (s: TrackStation) => at(s, 0, ohle.topWireHeightAboveRail);

  for (const i of mastIdx) {
    const s = stations[i];
    // Mast: from the ground beside the track up to mastHeight above rail level.
    out.masts.push({
      base: at(s, ohle.mastOffset, -railHeightAboveGround),
      top: at(s, ohle.mastOffset, ohle.mastHeight),
    });
    // Horizontal cantilever arm: mast → centreline at the top-wire height.
    const tip = at(s, 0, ohle.cantileverArmHeight);
    out.arms.push({ from: at(s, ohle.mastOffset, ohle.cantileverArmHeight), to: tip });
    // Rising arm: from lower on the mast up to the cantilever tip.
    out.arms.push({ from: at(s, ohle.mastOffset, ohle.risingArmRootHeight), to: tip });
  }

  // Wires: one straight span per consecutive mast pair (tensioned, no sag).
  for (let m = 0; m < mastIdx.length - 1; m++) {
    const a = stations[mastIdx[m]];
    const b = stations[mastIdx[m + 1]];
    out.contact.push({ points: [contactAt(a), contactAt(b)] });
    out.top.push({ points: [topAt(a), topAt(b)] });
  }

  return out;
}
